import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Navigation } from "lucide-react";
import {
  CONTACT,
  HOURS,
  CALL_LINK,
  WHATSAPP_LINK,
  MAPS_SEARCH_URL,
  MAPS_DIRECTIONS_URL,
} from "../data";
import { WhatsAppIcon } from "./WhatsAppIcon";

const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
  CONTACT.mapsQuery,
)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

const computeOpen = () => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chihuahua",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const wd = parts.find((p) => p.type === "weekday").value;
    let h = parseInt(parts.find((p) => p.type === "hour").value, 10);
    const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
    if (h === 24) h = 0;
    const cur = h * 60 + m;
    const today = HOURS.find((d) => d.key === wd);
    return !!(today && !today.closed && cur >= today.o && cur < today.c);
  } catch (e) {
    return null;
  }
};

export const Location = () => {
  const [isOpen, setIsOpen] = useState(computeOpen());

  useEffect(() => {
    const id = setInterval(() => setIsOpen(computeOpen()), 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="ubicacion"
      data-testid="location-section"
      className="relative py-24 md:py-32 bg-brand-base"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="relative border border-brand-gold/25 min-h-[380px] overflow-hidden"
          >
            <iframe
              title="Ubicación La Herencia Castañón"
              data-testid="google-map"
              src={mapSrc}
              className="w-full h-full min-h-[380px] grayscale-[40%] contrast-110"
              style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col"
          >
            <p className="text-brand-gold uppercase tracking-[0.3em] text-xs mb-4">
              Visítanos
            </p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-brand-cream leading-tight">
              Ubicación, horarios y contacto
            </h2>

            <div className="mt-8 space-y-6">
              <div className="flex gap-4">
                <MapPin className="w-6 h-6 text-brand-gold shrink-0 mt-1" />
                <div>
                  <h4 className="font-display text-lg font-bold text-brand-cream">
                    Dirección
                  </h4>
                  <a
                    href={MAPS_SEARCH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="location-address-link"
                    className="text-brand-sand hover:text-brand-gold transition-colors underline decoration-brand-gold/40 underline-offset-4"
                  >
                    {CONTACT.address}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <Phone className="w-6 h-6 text-brand-gold shrink-0 mt-1" />
                <div>
                  <h4 className="font-display text-lg font-bold text-brand-cream">
                    Teléfono / WhatsApp
                  </h4>
                  <a
                    href={CALL_LINK}
                    data-testid="location-phone"
                    className="text-brand-sand hover:text-brand-gold transition-colors"
                  >
                    {CONTACT.phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <Clock className="w-6 h-6 text-brand-gold shrink-0 mt-1" />
                <div className="w-full">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h4 className="font-display text-lg font-bold text-brand-cream">
                      Horarios de atención
                    </h4>
                    {isOpen !== null && (
                      <span
                        data-testid="open-status-badge"
                        className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                          isOpen
                            ? "bg-[#06C167]/15 text-[#06C167] border border-[#06C167]/40"
                            : "bg-brand-red/15 text-brand-red border border-brand-red/40"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isOpen ? "bg-[#06C167] animate-pulse" : "bg-brand-red"
                          }`}
                        />
                        {isOpen ? "Abierto ahora" : "Cerrado"}
                      </span>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {HOURS.map((h) => (
                      <li
                        key={h.day}
                        className="flex items-center justify-between border-b border-brand-gold/10 pb-2 text-sm"
                      >
                        <span className="text-brand-cream">{h.day}</span>
                        <span
                          className={
                            h.closed
                              ? "text-brand-red font-medium uppercase text-xs tracking-wider"
                              : "text-brand-sand"
                          }
                        >
                          {h.time}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-8">
              <a
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-btn"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-base font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-brand-cream transition-colors"
              >
                <Navigation className="w-4 h-4" /> Cómo llegar
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 border border-brand-gold text-brand-gold font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-brand-gold hover:text-brand-base transition-colors"
              >
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={CALL_LINK}
                data-testid="location-call-btn"
                className="inline-flex items-center justify-center gap-2 border border-brand-gold text-brand-gold font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-brand-gold hover:text-brand-base transition-colors"
              >
                <Phone className="w-4 h-4" /> Llamar
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
